import { push } from "connected-react-router";
import { useState, useMemo } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { combineAddress, fullName } from "../../app/utils";
import {
  APPLICATION_MODE_NEW,
  APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  DOCUMENT_TYPE_INSURANCE_PROOF,
  ROUTE_PATH,
  VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT,
} from "../../app/constants";
import {
  Header,
  Button,
  Paragraph,
  LineSeperator,
} from "../../components/RegularComponents";
import { CompanyContactInformation } from "./CompanyContactInformation";
import { CompanyFinancialLocation } from "./CompanyFinancialLocation";
import { AppointedAgentServiceAddress } from "./AppointedAgentServiceAddress";
import { CompanyFiscalEndDate } from "./CompanyFiscalEndDate";
import { UploadCriminalRecordCheck } from "./UploadCriminalRecordCheck";
import { MraRJSCEnterCompanyInfo } from "./MraRJSCEnterCompanyInfo";
import { MraRJSCEnterCompanyOfficals } from "./MraRJSCEnterCompanyOfficials";
import ReviewForm from "../../components/ReviewForm/ReviewForm";
import TopNavigationBar from "../TopNavigationBar";
import { setMortgageProperty } from "../../redux/slice/primaryAgencyApplicationSlice";
import { UploadProofOfInsurance } from "./UploadProofOfInsurance";
import { mraPrincipalBrokerSelector } from "../../redux/selector/mraPrincipalBrokerSelector";
import { saveBusinessLicenceApplicationData } from "../../redux/slice/businessLicenceApplicationSlice";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink,
} from "../../components/DocumentViewer/DocumentFileComponent";
import { convertFileDataForUrl } from "../../app/documentUploadUtils";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import { rjscOfficerObjFromEntityId } from "../../redux/slice/applicationsUtils";
import { fetchDocumentData } from "../../redux/slice/supportingDocumentsSlice";

const MORTGAGE_APPLICATION_PROPERTY = "userApplicationReviewComplete";

export const BrokerApplicationCompanyReview = (props) => {
  const dispatch = useDispatch();

  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const applicationType = APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE;
  const applicationMode = APPLICATION_MODE_NEW;

  const { mraApplicationState, documents } = useSelector((state) => ({
    mraApplicationState:
      state.primaryAgencyApplication.application?.businessLicenceData ?? {},
    documents: state.primaryAgencyApplication.application?.documents ?? [],
  }));

  const uploadCriminalRecordCheck = documents.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD &&
      document.changeType !== "DELETE"
  );
  const uploadedProofOfInsurance = documents.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_INSURANCE_PROOF &&
      document.changeType !== "DELETE"
  );

  const principalBroker = useSelector(mraPrincipalBrokerSelector);
  const mraSelectPrincipleBroker =
    principalBroker?.principalBrokerApplication?.businessLicenceData
      ?.personData ?? {};

  const {
    mainBusinessContact,
    financialRecordsLocation,
    responsibleAgent,
    serviceBusinessAddress,
    companyFiscalEndDate,
    businessName,
    companyRjscDetails,
    companyTrademark,
  } = mraApplicationState;

  const {
    businessEntity,
    businessData,
    businessContact,
    status,
    notRegistered,
  } = companyRjscDetails;

  const peopleList = useMemo(() => {
    if (businessData?.peopleList) {
      return businessData?.peopleList;
    }

    return [];
  }, [businessData]);

  const [editComponent, setEditComponent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const COMPONENT_KEYS = {
    RJSC_COMPANY_INFO: "rjscCompanyInfo",
    RJSC_COMPANY_OFFICIALS: "rjscCompanyOfficals",
    COMPANY_CONTACT: "companyContact",
    PRINCIPLE_BROKER: "principleBroker",
    FINANCIAL_LOCATION: "financialLocaiton",
    APPOINTED_AGENT: "appointedAgent",
    FISCAL_END_DATE: "ficalEndDate",
    CRIMINAL_RECORD: "criminalRecord",
    PROOF_INSURANCE_OMISSIONS: "proofInsuranceOmissions",
  };

  const formatDate = (date) => {
    const months = {
      0: "January",
      1: "February",
      2: "March",
      3: "April",
      4: "May",
      5: "June",
      6: "July",
      7: "August",
      8: "September",
      9: "October",
      10: "November",
      11: "December",
    };

    const dateObj = new Date(date);

    return `${months[dateObj.getMonth()]} ${dateObj.getDate()}`;
  };

  const MainSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{ borderColor: "#777", borderWidth: "3px" }}
      />
    );
  };

  const SubSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{
          borderColor: "#0058a466",
          borderWidth: "3px",
          width: "60%",
          marginLeft: "0px",
        }}
      />
    );
  };

  const titleForComponent = (componentKey) => {
    switch (componentKey) {
      case COMPONENT_KEYS.RJSC_COMPANY_INFO:
        return "Enter Company Information";
      case COMPONENT_KEYS.RJSC_COMPANY_OFFICIALS:
        return "Enter Company Offical Information";
      case COMPONENT_KEYS.COMPANY_CONTACT:
        return "Company Contact";
      case COMPONENT_KEYS.PRINCIPLE_BROKER:
        return "Principal Broker";
      case COMPONENT_KEYS.FINANCIAL_LOCATION:
        return "Financial Location";
      case COMPONENT_KEYS.APPOINTED_AGENT:
        return "Appointed Agent";
      case COMPONENT_KEYS.FISCAL_END_DATE:
        return "Fiscal End Date";
      case COMPONENT_KEYS.CRIMINAL_RECORD:
        return "Criminal Record";
      case COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS:
        return "Proof of Errors and Omissions Insurance";
      default:
        return "";
    }
  };

  const documentReviewComponent = (file) => {
    const fileComponents = [];

    if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
      fileComponents.push(
        <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
          <DocumentViewerFileNameLink
            id={"document-view-link-" + file.fileName}
            key={"document-view-link-" + file.fileName}
            file={file}
            onClick={() => {
              if (file.data) {
                let fileDataUrl = convertFileDataForUrl(
                  file.data,
                  file.documentContentType
                );
                setDocumentViewerDocument(fileDataUrl);
                setSelectedFile(file);
                setOpenDocumentViewer(true);
              } else {
                // download file data and then show it
                dispatch(fetchDocumentData(file.guid))
                  .then(downloadFile => {
                      if(downloadFile) { 
                          let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                          setDocumentViewerDocument(fileDataUrl)
                          setSelectedFile(downloadFile)
                          setOpenDocumentViewer(true)
                      }
                  })
                  .catch((error) => {
                      console.error(error)
                  })
              }
            }}
          />
        </div>
      );
    } else {
      fileComponents.push(
        <DownloadFileNameLink
          id={"download-link-" + file.fileName}
          componentKey={"download-link-" + file.fileName}
          file={file}
        />
      );
    }

    return fileComponents;
  };

  /*=== click handlers == */
  const handleEditRJSCCompanyInfo = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.RJSC_COMPANY_INFO);
  };

  const handleEditRJSCCompanyOfficial = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.RJSC_COMPANY_OFFICIALS);
  };

  const handleEditCompanyContact = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.COMPANY_CONTACT);
  };

  const handleEditFinancialLocation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FINANCIAL_LOCATION);
  };

  const handleEditAppointedAgent = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPOINTED_AGENT);
  };

  const handleEditFiscalEndDate = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FISCAL_END_DATE);
  };

  const handleEditCriminalRecord = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CRIMINAL_RECORD);
  };

  const handleEditProofInsurance = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS);
  };

  return (
    <>
      <div className={"container"}>
        <TopNavigationBar {...props} />
        <div className="row">
          <div className="col-xs-12 col-lg-9">
            <Header
              id="broker-application-review-header"
              header="Application Review"
            />
            <Paragraph
              id="broker-application-review-text"
              text={[
                "Review the information you provided in your application before continuing to the next step",
              ]}
            />
          </div>
        </div>

        <div className="timeline row">
          <ol>
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <span className="step-counter step-counter-circle">1</span>
                <div className="row">
                  <div className="col-xs-10">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Application<h3>"]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-3">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["Licence Type"]}
                    />
                  </div>
                  <div className="col-lg-3">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<i>Mortgage Brokerage Licence</i>"]}
                    />
                  </div>
                </div>
              </div>
            </li>

            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">2</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Brokerage Information<h3>"]}
                    />
                  </div>
                  <div className="col-lg-1">
                    {notRegistered && (
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditRJSCCompanyInfo}
                      />
                    )}
                  </div>
                </div>
                {/* {Should be taken from the RJSC slice?} */}
                <div className="row">
                  <div className="col-lg-3">
                    <Paragraph
                      id="broker-application-review-text"
                      text={[
                        "Legal Business Name",
                        "Operating Name",
                        "Trade Mark",
                        "RJSC Number",
                        "Business Licence Status",
                        "Registered Office",
                      ]}
                    />
                  </div>
                  <div className="col-lg-9">
                    <Paragraph
                      id="broker-application-review-text"
                      text={[
                        `${businessData?.businessOwner ?? businessName}`,
                        `${businessData?.businessOwner ? businessEntity?.businessName : ""}`,
                        companyTrademark ? companyTrademark : "<i>N/A</i>",
                        `${businessData?.relatedTo ?? businessEntity?.registryNumber}`,
                        `${status}`,
                        `${combineAddress(businessContact?.civicAddress)}`
                      ]}
                    />
                  </div>
                </div>
                <SubSeparator />
                <div className="row">
                  <div className="col-lg-12">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h4>Officals listed under RJSC?</h4>"]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-11">
                    {peopleList.map((person, index) => (
                      <>
                        <div className="row">
                          <div className="col-lg-3">
                            <Paragraph
                              id="broker-application-review-text"
                              text={["Name", "Relationship"]}
                            />
                          </div>
                          <div className="col-lg-3">
                            <Paragraph
                              id="broker-application-review-text"
                              text={[fullName(person), person.position]}
                            />
                          </div>
                        </div>
                        {index < peopleList.length - 1 && <SubSeparator />}
                      </>
                    ))}
                  </div>
                  <div className="col-lg-1">
                    {notRegistered && (
                      <Button
                        style={{ marginTop: "10px" }}
                        buttonType={"regular_primary"}
                        text={"Edit"}
                        onClick={handleEditRJSCCompanyOfficial}
                      />
                    )}
                  </div>
                </div>
                <MainSeparator />
              </div>
            </li>
            {/* {Company Contact} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Company Contact<h3>"]}
                    />
                    <ReviewForm data={{
                      "Phone" : mainBusinessContact?.phoneNumber ?? "",
                      "Email" : mainBusinessContact?.emailAddress ?? "",
                    }} />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditCompanyContact}
                    />
                  </div>
                </div>
              </div>
            </li>
            {/* {Principle Broker} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">3</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Principal Broker<h3>"]}
                    />
                    <ReviewForm
                      data={{
                        Name: fullName(mraSelectPrincipleBroker),
                      }}
                    />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={() => {}}
                    />
                  </div>
                </div>
              </div>
            </li>
            {/* {Financial Record Location} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">4</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Financial Record Location<h3>"]}
                    />
                    <ReviewForm data={{
                      "Civic address" : financialRecordsLocation?.street ?? '',
                      "City/Town" : financialRecordsLocation?.city ?? '',
                      "Province" : financialRecordsLocation?.province ?? '',
                      "Postal code" : financialRecordsLocation?.postalCode ?? '',
                    }} />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditFinancialLocation}
                    />
                  </div>
                </div>
              </div>
            </li>
            {/* {Appointed Agent} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">5</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Appointed agent<h3>"]}
                    />
                    <ReviewForm data={{
                      "Name" : fullName(responsibleAgent)
                    }} />
                    <LineSeperator />
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Service address<h3>"]}
                    />
                    <ReviewForm data={{
                      "Civic address" : serviceBusinessAddress?.street ?? "",
                      "City/Town" : serviceBusinessAddress?.city ?? '',
                      "Province" : serviceBusinessAddress?.province ?? '',
                      "Postal code" : serviceBusinessAddress?.postalCode ?? ''
                    }} />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditAppointedAgent}
                    />
                  </div>
                </div>
              </div>
            </li>
            {/* {Fiscal End Date} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">6</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Fiscal year end date<h3>"]}
                    />
                    <ReviewForm
                      data={{ "Date": formatDate(companyFiscalEndDate) }}
                    />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditFiscalEndDate}
                    />
                  </div>
                </div>
              </div>
            </li>
            {/* {Criminal Record Check} */}
            <li className="timeline-item col-md-12">
              <div className="step-content col-md-12 col-xs-12">
                <MainSeparator />
                <span className="step-counter step-counter-circle">7</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Criminal Record Check<h3>"]}
                    />
                    {Array.isArray(uploadCriminalRecordCheck) &&
                      uploadCriminalRecordCheck.map((element, index) => {
                        const rjscOfficer = rjscOfficerObjFromEntityId(
                                  peopleList,element.entityId)
                        return (
                          <>
                            <ReviewForm
                              data={{
                                Name: fullName(rjscOfficer),
                                File: documentReviewComponent(element),
                                Position: rjscOfficer?.position ?? ""
                              }}
                            />
                            {index < uploadCriminalRecordCheck.length - 1 && (
                              <SubSeparator />
                            )}
                          </>
                        )
                      })}
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditCriminalRecord}
                    />
                  </div>
                </div>
                <MainSeparator />
              </div>
            </li>
            {/* {Proof of Insurance and Omissions} */}
            <li className="timeline-item col-md-12">
              <div className="step-content step-content-no-border col-md-12 col-xs-12">
                <span className="step-counter step-counter-circle">8</span>
                <div className="row">
                  <div className="col-lg-11">
                    <Paragraph
                      style={{ paddingTop: "10px" }}
                      id="broker-application-review-text"
                      text={["<h3>Proof of Errors and Omissions Insurance<h3>"]}
                    />
                    <ReviewForm
                      data={{
                        File:
                          uploadedProofOfInsurance.length > 0
                            ? documentReviewComponent(
                                uploadedProofOfInsurance[0]
                              )
                            : "",
                      }}
                    />
                  </div>
                  <div className="col-lg-1">
                    <Button
                      style={{ marginTop: "10px" }}
                      buttonType={"regular_primary"}
                      text={"Edit"}
                      onClick={handleEditProofInsurance}
                    />
                  </div>
                </div>
              </div>
            </li>
          </ol>
        </div>

        <div className="row">
          <div className="col-lg-4">
            <Button
              id="complete-mortgage-education-continue-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={"Save and next"}
              onClick={() => {
                // Set MORTGAGE PROP value
                dispatch(
                  setMortgageProperty({
                    key: MORTGAGE_APPLICATION_PROPERTY,
                    value: true,
                  })
                );

                dispatch(
                  saveBusinessLicenceApplicationData(
                    applicationType,
                    applicationMode
                  )
                )
                  .then((data) => {
                    // On to the next item
                    dispatch(
                      push(
                        `${ROUTE_PATH.MRA_APP}${ROUTE_PATH.MRA_BROKER_NEXT_STEPS}`
                      )
                    );
                  })
                  .catch((err) => {
                    // TODO : error check
                  });
              }}
            />
          </div>
        </div>
      </div>
      <Modal
        appElement={document.getElementById("root")}
        isOpen={showEditDialog}
        contentLabel="editDetailsDialog"
        onRequestClose={() => {
          setShowEditDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent editDetailsModalDialog"
        closeTimeoutMS={150}
      >
        <div className="row">
          <div className="col-11">
            <div className="row">
              <div className="col-12">
                <h2>{titleForComponent(editComponent)}</h2>
                <MainSeparator />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.RJSC_COMPANY_INFO && (
                  <MraRJSCEnterCompanyInfo
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.RJSC_COMPANY_OFFICIALS && (
                  <MraRJSCEnterCompanyOfficals
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPANY_CONTACT && (
                  <CompanyContactInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FINANCIAL_LOCATION && (
                  <CompanyFinancialLocation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPOINTED_AGENT && (
                  <AppointedAgentServiceAddress
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FISCAL_END_DATE && (
                  <CompanyFiscalEndDate
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CRIMINAL_RECORD && (
                  <UploadCriminalRecordCheck
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS && (
                  <UploadProofOfInsurance
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="col-1">
            <div
              className="closeButton pull-right"
              onClick={() => {
                setShowEditDialog(false);
              }}
            >
              <i className="fa fa-2x fa-times"></i>
            </div>
          </div>
        </div>
      </Modal>

      <DocumentViewer
        fileData={documentViewerDocument}
        fileName={selectedFile?.filename}
        isOpen={openDocumentViewer}
        handleClose={() => setOpenDocumentViewer(false)}
      />
    </>
  );
};
